import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from 'axios';
import EventModal from './EventModal';
import { BsPlusCircleFill } from 'react-icons/bs';
import { CiEdit, CiCalendar } from 'react-icons/ci';
import { FaTrash, FaMapMarkerAlt, FaFilePdf, FaImage, FaAward, FaCalendarAlt, FaHistory, FaBuilding, FaLink, FaUserFriends, FaTimes, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const localizer = momentLocalizer(moment);

interface EventItem {
  id: number | string;
  location: string;
  chapter: string;
  event_type: string;
  event_date: string;
  description: string;
  rmbian_of_meeting?: string;
  presentation_1?: string;
  presentation_2?: string;
  speaker_designation?: string;
  photographs?: string;
  mom_pdf?: string;
  instagram_reel?: string;
  heading?: string;
  venue?: string;
}

interface CalendarEvent {
  id: number | string;
  title: string;
  start: Date;
  end: Date;
  resource: EventItem;
}

const getEventTypeColor = (type: string) => {
  switch (type) {
    case 'Meeting': return '#3B82F6';
    case 'Field Visit': return '#10B981';
    case 'General Meeting': return '#8B5CF6';
    case 'Installation': return '#F59E0B';
    case 'Social': return '#EC4899';
    case 'Webinar': return '#6366F1';
    default: return '#6B7280';
  }
};

interface EventsCalendarProps {
  isMemberPortal?: boolean;
}

const EventsCalendar: React.FC<EventsCalendarProps> = ({ isMemberPortal = false }) => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTextNewsOpen, setIsTextNewsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [timelineFilter, setTimelineFilter] = useState<'all' | 'upcoming' | 'past'>('all');

  const [rsvpModalEventId, setRsvpModalEventId] = useState<string | null>(null);
  const [rsvpModalData, setRsvpModalData] = useState<{ event: any; members: any[]; rsvps: any[] } | null>(null);
  const [loadingRsvps, setLoadingRsvps] = useState(false);
  const [rsvpTabFilter, setRsvpTabFilter] = useState<'ALL' | 'PENDING'>('ALL');

  const handleOpenRsvps = async (eventId: string | number) => {
    const formattedId = String(eventId).startsWith('cal_') ? String(eventId) : `cal_${eventId}`;
    setRsvpModalEventId(formattedId);
    setLoadingRsvps(true);
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/event-rsvp/details/${formattedId}`);
      if (res.data.success) {
        setRsvpModalData({ event: res.data.event, members: res.data.members || [], rsvps: res.data.rsvps || [] });
      }
    } catch (err) {
      console.error('Error loading RSVPs:', err);
    } finally {
      setLoadingRsvps(false);
    }
  };

  const sendVisitorWhatsApp = (phone: string, name: string, status: string, eventTitle?: string) => {
    let p = (phone || '').replace(/\D/g, '');
    if (!p) {
      alert('No mobile number available for this visitor.');
      return;
    }
    if (p.length === 10) p = '91' + p;

    const isApproved = status === 'VERIFIED';
    const msg = isApproved
      ? `Hello ${name}! 🎉 Your visitor registration ${eventTitle ? `for "${eventTitle}"` : ''} has been VERIFIED & APPROVED by RMB Admin. We look forward to welcoming you! - RMB Jaipur Chapter`
      : `Hello ${name}. Update regarding your visitor registration ${eventTitle ? `for "${eventTitle}"` : ''}: Your registration status has been updated to REJECTED. Please contact RMB Admin for any queries. - RMB Jaipur Chapter`;

    const url = `https://api.whatsapp.com/send?phone=${p}&text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  const sendVisitorEmail = (email: string, name: string, status: string, eventTitle?: string) => {
    if (!email || !email.includes('@')) {
      alert('No valid email address available for this visitor.');
      return;
    }

    const isApproved = status === 'VERIFIED';
    const subj = isApproved
      ? `✅ Visitor Registration Approved - ${eventTitle || 'RMB Chapter Meeting'}`
      : `Update: Visitor Registration Status - ${eventTitle || 'RMB Chapter Meeting'}`;

    const body = isApproved
      ? `Dear ${name},\n\nWe are pleased to inform you that your visitor registration ${eventTitle ? `for "${eventTitle}"` : ''} has been VERIFIED & APPROVED by the RMB Chapter Admin.\n\nStatus: APPROVED & CONFIRMED\n\nWe look forward to meeting you!\n\nBest regards,\nRMB Jaipur Chapter`
      : `Dear ${name},\n\nThis is an update regarding your visitor registration ${eventTitle ? `for "${eventTitle}"` : ''}. Your registration status has been updated to REJECTED.\n\nIf you have any questions, please contact RMB Admin.\n\nBest regards,\nRMB Jaipur Chapter`;

    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const handleVerifyRsvp = async (r: any, status: 'VERIFIED' | 'REJECTED') => {
    const vName = r.visitor_name || r.member_name || 'Visitor';
    const vPhone = r.visitor_phone || '';
    const eventTitle = rsvpModalData?.event?.title || '';

    const actionText = status === 'VERIFIED' ? 'accept & verify' : 'reject';
    if (!window.confirm(`Are you sure you want to ${actionText} the visitor registration for "${vName}"?`)) {
      return;
    }
    try {
      const res = await axios.put(`https://rmbbackend.rmbjaipur.com/auth/event-rsvp/${r.id}/verify`, {
        verification_status: status
      });
      if (res.data.success) {
        alert(`✅ Visitor registration marked as ${status === 'VERIFIED' ? 'ACCEPTED & VERIFIED' : 'REJECTED'}!`);
        if (rsvpModalEventId) {
          handleOpenRsvps(rsvpModalEventId);
        }

        if (vPhone && window.confirm(`📱 Would you like to send a WhatsApp status update message to ${vName} (${vPhone}) now?`)) {
          sendVisitorWhatsApp(vPhone, vName, status, eventTitle);
        }
      }
    } catch (err) {
      console.error('Error verifying visitor transaction:', err);
      alert('Failed to update verification status.');
    }
  };

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const getEventParsedDate = (dStr?: string) => {
    if (!dStr) return null;
    let s = String(dStr).trim();
    if (s.includes(' ') && !s.includes('T')) s = s.replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const parts = s.split('-');
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 23, 59, 59);
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  const upcomingCount = events.filter(e => {
    const d = getEventParsedDate(e.event_date);
    return d ? d >= todayStart : false;
  }).length;

  const pastCount = events.filter(e => {
    const d = getEventParsedDate(e.event_date);
    return d ? d < todayStart : false;
  }).length;

  const filteredEvents = events.filter(e => {
    if (timelineFilter === 'all') return true;
    const evtDate = getEventParsedDate(e.event_date);
    if (!evtDate) return false;
    return timelineFilter === 'upcoming' ? evtDate >= todayStart : evtDate < todayStart;
  });

  const fetchEvents = async () => {
    try {
      // Only fetch from /auth/events — news posts are separate manual items shown in the Blog/News section.
      // Merging both caused duplicate entries since POST /events previously auto-inserted into news table.
      const eventsRes = await axios.get<any[]>('https://rmbbackend.rmbjaipur.com/auth/events').catch(() => ({ data: [] }));
      const eventsList = Array.isArray(eventsRes.data) ? eventsRes.data : [];

      const unifiedEvents: EventItem[] = eventsList.map((e: any) => ({
        id: e.id,
        location: e.location || e.venue || 'Jaipur',
        chapter: e.chapter || 'RMB Jaipur',
        event_type: e.event_type || 'General Meeting',
        event_date: e.event_date || e.start_date || e.created_at,
        description: e.description || e.heading || e.content || 'Chapter Meeting & Event',
        heading: e.heading || e.title || e.description || 'Chapter Event',
        rmbian_of_meeting: e.rmbian_of_meeting || e.RMBiansOfMeeting || '',
        presentation_1: e.presentation_1 || '',
        presentation_2: e.presentation_2 || '',
        speaker_designation: e.speaker_designation || '',
        photographs: e.photographs || e.photos || '',
        mom_pdf: e.mom_pdf || '',
        instagram_reel: e.instagram_reel || ''
      }));

      setEvents(unifiedEvents);

      const mapped: CalendarEvent[] = unifiedEvents.map((evt, idx) => {
        const rawDate = evt.event_date;
        const start = rawDate ? new Date(rawDate) : new Date();
        const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
        return {
          id: typeof evt.id === 'number' ? evt.id : idx + 9000,
          title: `[${evt.event_type}] ${evt.heading || evt.description}`,
          start: isNaN(start.getTime()) ? new Date() : start,
          end: isNaN(end.getTime()) ? new Date() : end,
          resource: evt
        };
      });

      setCalendarEvents(mapped);
    } catch (error) {
      console.error('Error fetching events/news:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSelectSlot = ({ start }: { start: Date }) => {
    setSelectedEvent(null);
    setSelectedDate(start);
    setIsModalOpen(true);
  };

  const handleSelectEvent = (calEvent: CalendarEvent) => {
    setSelectedEvent(calEvent.resource);
    setSelectedDate(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number | string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/events/${id}`);
        fetchEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Failed to delete event.');
      }
    }
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const backgroundColor = getEventTypeColor(event.resource.event_type);
    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.95,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        fontWeight: 'bold',
        padding: '3px 6px'
      }
    };
  };

  return (
    <div className="p-3 sm:p-6 bg-slate-50 min-h-screen space-y-4 sm:space-y-8 max-w-full overflow-x-hidden">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 sm:p-5 rounded-2xl shadow-sm border border-slate-200 gap-3 sm:gap-4">
        <div>
          <span className="text-[10px] sm:text-xs font-black uppercase text-amber-600 tracking-wider">Admin Schedule Dashboard</span>
          <h1 className="text-xl sm:text-2xl font-black text-[#02256D]">Events & Meetings Calendar</h1>
          <p className="text-[11px] sm:text-xs text-slate-500 font-medium">Interactive schedule calendar and detailed listing of all chapter events.</p>
        </div>

        {!isMemberPortal && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={() => setIsTextNewsOpen(true)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#FDBC12] hover:bg-amber-400 text-[#02256D] font-extrabold px-3.5 py-2.5 rounded-xl shadow-md transition cursor-pointer text-xs"
            >
              <BsPlusCircleFill size={14} /> Add Text News
            </button>

            <button
              onClick={() => {
                setSelectedEvent(null);
                setSelectedDate(null);
                setIsModalOpen(true);
              }}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#02256D] hover:bg-blue-900 text-[#FDBC12] font-extrabold px-4 py-2.5 rounded-xl shadow-md transition cursor-pointer text-xs"
            >
              <BsPlusCircleFill size={14} /> Add New Event
            </button>
          </div>
        )}
      </div>

      {/* Legend Badges */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2.5 bg-white p-3 sm:p-4 rounded-xl border border-slate-200 text-[11px] sm:text-xs font-bold shadow-xs overflow-x-auto no-scrollbar">
        <span className="text-slate-500 self-center mr-1 uppercase text-[10px] sm:text-[11px] tracking-wider w-full sm:w-auto mb-1 sm:mb-0">Event Categories:</span>
        {['Meeting', 'Field Visit', 'General Meeting', 'Installation', 'Social', 'Webinar', 'Other'].map(type => (
          <span key={type} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-white shadow-xs text-[10px] sm:text-xs" style={{ backgroundColor: getEventTypeColor(type) }}>
            <span className="w-1.5 h-1.5 rounded-full bg-white opacity-80"></span>
            {type}
          </span>
        ))}
      </div>

      {/* 1. INTERACTIVE CALENDAR VIEW (Responsive Height for Mobile) */}
      <div className="bg-white p-2 sm:p-6 rounded-2xl shadow-lg border border-slate-200 h-[480px] sm:h-[680px] w-full overflow-hidden">
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          selectable
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          views={['month', 'week', 'day', 'agenda']}
          defaultView="month"
        />
      </div>

      {/* 2. DETAILED EVENTS LIST UNDER THE CALENDAR */}
      <div className="space-y-4 pt-2 sm:pt-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-200 pb-3 gap-3">
          <div>
            <span className="text-[10px] sm:text-xs font-black uppercase text-amber-600 tracking-wider">Schedule Records</span>
            <h2 className="text-lg sm:text-xl font-black text-[#02256D] flex items-center gap-2">
              <CiCalendar className="text-xl sm:text-2xl text-amber-500" /> Scheduled Meetings & Events ({filteredEvents.length})
            </h2>
          </div>

          {/* Timeline Filter Segmented Control */}
          <div className="flex flex-wrap bg-slate-100 p-1 sm:p-1.5 rounded-xl border border-slate-200 text-xs font-black w-full sm:w-auto gap-1">
            <button
              onClick={() => setTimelineFilter('all')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition flex items-center justify-center gap-1 cursor-pointer text-[11px] sm:text-xs ${
                timelineFilter === 'all'
                  ? 'bg-[#02256D] text-[#FDBC12] shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span>🌐 All Events</span>
              <span className="px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{events.length}</span>
            </button>

            <button
              onClick={() => setTimelineFilter('upcoming')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition flex items-center justify-center gap-1 cursor-pointer text-[11px] sm:text-xs ${
                timelineFilter === 'upcoming'
                  ? 'bg-emerald-700 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FaCalendarAlt size={11} />
              <span>🗓️ Upcoming</span>
              <span className="px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{upcomingCount}</span>
            </button>

            <button
              onClick={() => setTimelineFilter('past')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition flex items-center justify-center gap-1 cursor-pointer text-[11px] sm:text-xs ${
                timelineFilter === 'past'
                  ? 'bg-[#0A2451] text-amber-400 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FaHistory size={11} />
              <span>📜 Past Events</span>
              <span className="px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{pastCount}</span>
            </button>
          </div>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="bg-white p-8 rounded-2xl text-center text-slate-500 border border-slate-200 font-medium">
            No {timelineFilter === 'upcoming' ? 'upcoming' : timelineFilter === 'past' ? 'past' : ''} events found in schedule records.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((evt) => {
              const now = new Date();
              const evtDate = evt.event_date ? new Date(evt.event_date) : null;
              const isPast = evtDate ? evtDate < now : false;

              const rawPhotos = evt.photographs || (evt as any).photos || (evt as any).gallery || (evt as any).img || (evt as any).image || (evt as any).thumbnail;
              let photoList: string[] = [];
              if (Array.isArray(rawPhotos)) {
                photoList = rawPhotos;
              } else if (typeof rawPhotos === 'string') {
                const trimmed = rawPhotos.trim();
                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                  try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed)) photoList = parsed;
                    else if (parsed) photoList = [String(parsed)];
                  } catch { photoList = [trimmed]; }
                } else if (trimmed.includes('&')) {
                  photoList = trimmed.split('&').filter(Boolean);
                } else if (trimmed) {
                  photoList = [trimmed];
                }
              }

              const cleanPhotos = photoList.map((p: any) => {
                if (!p) return '';
                const clean = String(p).replace(/\\/g, '/').replace(/^\/+/, '');
                return clean.startsWith('http') ? clean : `https://rmbbackend.rmbjaipur.com/${clean}`;
              }).filter(Boolean);

              const coverImg = cleanPhotos[0] || 'https://rmbbackend.rmbjaipur.com/uploads/default_news.jpg';

              return (
                <div key={evt.id} className="bg-white rounded-2xl border border-slate-200 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col justify-between">
                  <div>
                    {/* Cover Thumbnail & Badges */}
                    <div className="relative w-full min-h-[200px] max-h-[350px] bg-slate-950 overflow-hidden flex items-center justify-center border-b border-slate-200 p-1.5 group">
                      <img
                        src={coverImg}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-25 scale-110 pointer-events-none"
                      />
                      <img
                        src={coverImg}
                        alt={evt.heading || evt.description}
                        className="w-full h-auto max-h-[340px] object-contain relative z-10 mx-auto group-hover:scale-[1.02] transition duration-300 rounded-lg shadow-sm"
                        onError={(e) => { e.currentTarget.src = 'https://rmbbackend.rmbjaipur.com/uploads/default_news.jpg'; }}
                      />
                      <div className="absolute top-3 left-3 z-20 flex gap-2">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase text-white shadow-md" style={{ backgroundColor: getEventTypeColor(evt.event_type) }}>
                          {evt.event_type}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3 z-20">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase shadow-md flex items-center gap-1 ${
                          isPast ? 'bg-slate-800/90 text-amber-400 border border-amber-400/40' : 'bg-emerald-600/90 text-white'
                        }`}>
                          {isPast ? <FaHistory size={10} /> : <FaCalendarAlt size={10} />}
                          {isPast ? 'Past Event' : 'Upcoming'}
                        </span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-5 space-y-3">
                      <h3 className="font-black text-slate-800 text-base line-clamp-2 leading-snug">
                        {evt.heading || evt.description}
                      </h3>

                      {/* Event Description Snippet */}
                      {evt.description && evt.description !== (evt.heading || evt.description) && (
                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 font-medium">
                          {evt.description.replace(/<[^>]*>?/gm, '')}
                        </p>
                      )}

                      <div className="space-y-1.5 text-xs text-slate-600 font-medium">
                        <p className="flex items-center gap-1.5 text-blue-950 font-bold">
                          <CiCalendar className="text-amber-600 text-base" />
                          <span>{evtDate ? evtDate.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Scheduled Event'}</span>
                        </p>

                        {(evt.location || evt.venue) && (
                          <p className="flex items-center gap-1.5 text-slate-600">
                            <FaMapMarkerAlt className="text-rose-500" />
                            <span>{evt.location || evt.venue}</span>
                          </p>
                        )}

                        {evt.chapter && (
                          <p className="flex items-center gap-1.5 text-slate-500">
                            <FaBuilding className="text-blue-600" />
                            <span>{evt.chapter}</span>
                          </p>
                        )}
                      </div>

                      {/* Speakers & Presenters Info Box — contextual per event type */}
                      {evt.presentation_1 && evt.event_type !== 'Social' && (
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1 text-xs">
                          <span className="font-bold text-amber-700 block text-[11px]">
                            {evt.event_type === 'Installation'
                              ? '👑 Chief Guest:'
                              : evt.event_type === 'Webinar'
                              ? '🎙️ Keynote Speaker:'
                              : evt.event_type === 'Field Visit'
                              ? '📍 Field Visit Host Member:'
                              : '💼 Presentation 1:'}
                          </span>
                          <p className="text-slate-800 font-semibold">{evt.presentation_1}</p>
                          {evt.presentation_2 && (
                            <p className="text-slate-600 text-[11px] font-medium pt-0.5 border-t border-slate-200 mt-1">
                              {evt.event_type === 'Installation' ? `🏢 Organization: ${evt.presentation_2}` :
                               evt.event_type === 'Webinar' ? `🏢 Company: ${evt.presentation_2}` :
                               evt.event_type === 'Field Visit' ? `🏢 Business: ${evt.presentation_2}` :
                               `🎓 ${evt.presentation_2}`}
                            </p>
                          )}
                          {evt.event_type === 'Webinar' && (evt as any).speaker_designation && (
                            <p className="text-indigo-700 text-[11px] font-medium border-t border-slate-200 pt-0.5 mt-0.5">
                              🎓 Designation: {(evt as any).speaker_designation}
                            </p>
                          )}
                        </div>
                      )}

                      {/* RMBian of Meeting Badge */}
                      {evt.rmbian_of_meeting && (
                        <div className="bg-amber-50 p-2.5 rounded-xl border border-amber-200 text-xs flex items-center gap-2">
                          <FaAward className="text-amber-600 text-sm flex-shrink-0" />
                          <span className="font-bold text-amber-900">🏆 RMBian of Meeting: {evt.rmbian_of_meeting}</span>
                        </div>
                      )}

                      {/* Media Badges */}
                      <div className="flex flex-wrap items-center gap-2 text-xs pt-1">
                        {photoList.length > 0 && (
                          <span className="px-2.5 py-1 bg-blue-50 text-blue-800 rounded-lg border border-blue-200 font-bold flex items-center gap-1">
                            <FaImage /> {photoList.length} Photos
                          </span>
                        )}
                        {evt.mom_pdf && (
                          <a
                            href={`https://rmbbackend.rmbjaipur.com/${evt.mom_pdf.replace(/\\/g, '/')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="px-2.5 py-1 bg-rose-50 text-rose-800 rounded-lg border border-rose-200 font-bold flex items-center gap-1 hover:bg-rose-100"
                          >
                            <FaFilePdf /> MoM PDF
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card Action Footer */}
                  <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-wrap justify-between items-center gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {!isMemberPortal && (
                        <button
                          onClick={() => {
                            setSelectedEvent(evt);
                            setSelectedDate(null);
                            setIsModalOpen(true);
                          }}
                          className="px-2.5 py-1.5 bg-[#02256D] hover:bg-blue-900 text-[#FDBC12] text-xs font-bold rounded-lg transition flex items-center gap-1 cursor-pointer"
                        >
                          <CiEdit size={16} /> Edit
                        </button>
                      )}

                      <button
                        onClick={() => {
                          const link = `${window.location.origin}/event-rsvp/cal_${evt.id}`;
                          navigator.clipboard.writeText(link);
                          alert(`🔗 RSVP & Visitor Pre-Registration Link copied to clipboard!\n\nLink: ${link}`);
                        }}
                        className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg transition flex items-center gap-1 cursor-pointer shadow-sm"
                        title="Copy Public RSVP Link for Members & Visitors"
                      >
                        <FaLink size={12} /> Share Link
                      </button>

                      <button
                        onClick={() => handleOpenRsvps(evt.id)}
                        className="px-2.5 py-1.5 bg-purple-700 hover:bg-purple-600 text-white text-xs font-bold rounded-lg transition flex items-center gap-1 cursor-pointer shadow-sm"
                        title="View Member & Visitor Event Responses"
                      >
                        <FaUserFriends size={13} /> Responses
                      </button>
                    </div>

                    {!isMemberPortal && (
                      <button
                        onClick={() => handleDelete(evt.id)}
                        className="p-1.5 text-rose-600 hover:bg-rose-100 rounded-lg transition cursor-pointer ml-auto"
                        title="Delete Event"
                      >
                        <FaTrash size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* EVENT MODAL */}
      {isModalOpen && (
        <EventModal
          eventData={selectedEvent}
          selectedDate={selectedDate}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedEvent(null);
            setSelectedDate(null);
          }}
          onSuccess={() => {
            fetchEvents();
          }}
        />
      )}

      {/* TEXT NEWS TICKER MODAL */}
      {isTextNewsOpen && (
        <TextNewsModal onClose={() => setIsTextNewsOpen(false)} />
      )}

      {/* RSVP & VISITOR REGISTRATIONS MODAL */}
      {rsvpModalEventId && (() => {
        const totalActiveMembers = rsvpModalData?.members?.length || 0;
        const rsvpList = rsvpModalData?.rsvps || [];
        
        const memberRsvps = rsvpList.filter((r: any) => r.rsvp_status !== 'VISITOR_REGISTERED');
        const markedMemberCount = memberRsvps.length;

        const unmarkedMembers = (rsvpModalData?.members || []).filter((m: any) => (
          !memberRsvps.some((r: any) => Number(r.user_id) === Number(m.id) || (r.member_name && r.member_name.toLowerCase().trim() === (m.name || '').toLowerCase().trim()))
        ));
        const unmarkedMemberCount = unmarkedMembers.length;

        const registeredVisitors = rsvpList.filter((r: any) => r.rsvp_status === 'VISITOR_REGISTERED');
        const memberVisitorCount = memberRsvps.filter((r: any) => r.visitor_name && r.visitor_name.trim().length > 0).length;

        const verifiedVisitors = registeredVisitors.filter((r: any) => r.verification_status === 'VERIFIED');
        const pendingVisitors = registeredVisitors.filter((r: any) => !r.verification_status || r.verification_status === 'PENDING');
        const totalVisitorCount = verifiedVisitors.length + memberVisitorCount;

        return (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => { setRsvpModalEventId(null); setRsvpModalData(null); }}>
            <div className="bg-[#1E2B45] text-slate-100 rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="bg-[#0A2451] p-5 border-b border-[#2A3B5C] flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-black text-white flex items-center gap-2">
                    <FaUserFriends className="text-[#FDBC12]" /> Event Attendance & Visitor Pre-Registrations
                  </h3>
                  <p className="text-xs text-slate-300 font-medium">
                    {rsvpModalData?.event?.title || 'RMB Event'}
                  </p>
                </div>
                <button
                  onClick={() => { setRsvpModalEventId(null); setRsvpModalData(null); }}
                  className="w-8 h-8 bg-slate-800 hover:bg-rose-600 text-white rounded-full flex items-center justify-center transition cursor-pointer"
                >
                  <FaTimes />
                </button>
              </div>

              {/* 4 Stat Summary Cards */}
              <div className="p-4 bg-[#131E32] border-b border-[#2A3B5C] grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-xs">
                <div className="bg-[#1E2B45] p-3 rounded-xl border border-[#2A3B5C]">
                  <p className="text-slate-400 font-extrabold uppercase text-[10px]">Total Active Members</p>
                  <p className="text-lg font-black text-white mt-0.5">👥 {totalActiveMembers}</p>
                </div>

                <div className="bg-[#1E2B45] p-3 rounded-xl border border-emerald-500/40">
                  <p className="text-emerald-400 font-extrabold uppercase text-[10px]">RSVP Marked</p>
                  <p className="text-lg font-black text-emerald-300 mt-0.5">✅ {markedMemberCount}</p>
                </div>

                <div
                  onClick={() => setRsvpTabFilter('PENDING')}
                  className="bg-[#1E2B45] p-3 rounded-xl border border-rose-500/40 hover:bg-[#2A3B5C] transition cursor-pointer"
                  title="Click to view list of members who have not marked attendance"
                >
                  <p className="text-rose-400 font-extrabold uppercase text-[10px]">RSVP Pending (Not Marked)</p>
                  <p className="text-lg font-black text-rose-300 mt-0.5">⏳ {unmarkedMemberCount}</p>
                </div>

                <div className="bg-[#1E2B45] p-3 rounded-xl border border-cyan-500/40">
                  <p className="text-cyan-400 font-extrabold uppercase text-[10px]">Verified Visitors Count</p>
                  <p className="text-lg font-black text-cyan-300 mt-0.5">
                    🎟️ {totalVisitorCount} {pendingVisitors.length > 0 ? <span className="text-amber-400 text-xs font-normal">({pendingVisitors.length} Pending)</span> : null}
                  </p>
                </div>
              </div>

              {/* Tab Filters */}
              <div className="px-5 py-2.5 bg-[#0D1525] border-b border-[#2A3B5C] flex gap-3 text-xs font-bold">
                <button
                  onClick={() => setRsvpTabFilter('ALL')}
                  className={`px-4 py-1.5 rounded-lg transition cursor-pointer ${
                    rsvpTabFilter === 'ALL'
                      ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow'
                      : 'bg-[#1E2B45] text-slate-300 hover:bg-[#2A3B5C]'
                  }`}
                >
                  📋 Submitted Responses & Visitors ({rsvpList.length})
                </button>
                <button
                  onClick={() => setRsvpTabFilter('PENDING')}
                  className={`px-4 py-1.5 rounded-lg transition cursor-pointer ${
                    rsvpTabFilter === 'PENDING'
                      ? 'bg-rose-600 text-white font-black shadow'
                      : 'bg-[#1E2B45] text-slate-300 hover:bg-[#2A3B5C]'
                  }`}
                >
                  ⏳ Pending Members - Not Marked ({unmarkedMemberCount})
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 overflow-y-auto flex-1 space-y-3">
                {loadingRsvps ? (
                  <div className="p-8 text-center text-slate-400 font-bold animate-pulse">Loading Event RSVPs...</div>
                ) : rsvpTabFilter === 'PENDING' ? (
                  unmarkedMembers.length === 0 ? (
                    <div className="p-8 text-center text-emerald-400 font-bold">🎉 All active members have recorded their RSVP response for this event!</div>
                  ) : (
                    <div className="space-y-2.5">
                      <p className="text-xs text-rose-400 font-bold mb-2">The following {unmarkedMemberCount} active members have not yet submitted their RSVP for this event:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {unmarkedMembers.map((m: any) => (
                          <div key={m.id} className="p-3 bg-[#131E32] rounded-xl border border-rose-500/30 flex justify-between items-center text-xs">
                            <div>
                              <p className="font-extrabold text-white">{m.name}</p>
                              <p className="text-[11px] text-slate-400">{m.company_name || 'RMB Jaipur'} • 📞 {m.number || 'No Phone'}</p>
                            </div>
                            <span className="px-2 py-0.5 bg-rose-950 text-rose-300 border border-rose-500/40 rounded-full font-bold text-[10px]">
                              ⏳ Pending
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ) : (
                  rsvpList.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 font-bold">No member RSVPs or visitor registrations submitted yet for this event.</div>
                  ) : (
                    <div className="space-y-3">
                      {rsvpList.map((r: any) => {
                        const isVisitor = r.rsvp_status === 'VISITOR_REGISTERED';
                        const isVerified = r.verification_status === 'VERIFIED';
                        const isRejected = r.verification_status === 'REJECTED';

                        return (
                          <div key={r.id} className="p-4 bg-[#131E32] rounded-xl border border-[#2A3B5C] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-extrabold text-white text-sm">
                                  {isVisitor ? r.visitor_name : r.member_name}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full font-black text-[10px] uppercase ${
                                  isVisitor ? 'bg-cyan-950 text-cyan-300 border border-cyan-500/30' :
                                  r.rsvp_status === 'COMING' ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/30' :
                                  r.rsvp_status === 'CANT_COME' ? 'bg-rose-950 text-rose-300 border border-rose-500/30' :
                                  r.rsvp_status === 'COMING_WITH_VISITOR' ? 'bg-blue-950 text-blue-300 border border-blue-500/30' :
                                  'bg-amber-950 text-amber-300 border border-amber-500/30'
                                }`}>
                                  {isVisitor ? '🎟️ Registered Visitor' :
                                   r.rsvp_status === 'COMING' ? '✅ Coming' :
                                   r.rsvp_status === 'CANT_COME' ? '❌ Can\'t Come' :
                                   r.rsvp_status === 'COMING_WITH_VISITOR' ? '👥 Coming with Visitor' :
                                   '🔄 Substitute Joining'}
                                </span>

                                {isVisitor && (
                                  <span className={`px-2 py-0.5 rounded-full font-black text-[9px] uppercase ${
                                    isVerified ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/40' :
                                    isRejected ? 'bg-rose-950 text-rose-300 border border-rose-500/40' :
                                    'bg-amber-950 text-amber-300 border border-amber-500/40 animate-pulse'
                                  }`}>
                                    {isVerified ? '🟢 Transaction Verified' : isRejected ? '🔴 Transaction Rejected' : '🟡 Verification Pending'}
                                  </span>
                                )}
                              </div>

                              {isVisitor ? (
                                <div>
                                  <p className="text-slate-400 mt-1 font-medium">
                                    🏢 Company: {r.visitor_company || 'N/A'} • 📞 {r.visitor_phone || 'N/A'} • ✉️ {r.visitor_email || 'N/A'} • 👤 Invited by: <strong className="text-amber-300">{r.invited_by || 'Direct'}</strong>
                                  </p>

                                  {/* Admin Transaction Verification Action Buttons */}
                                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                                    {!isVerified && (
                                      <button
                                        onClick={() => handleVerifyRsvp(r, 'VERIFIED')}
                                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[11px] rounded-lg shadow transition cursor-pointer flex items-center gap-1"
                                      >
                                        ✅ Accept & Approve
                                      </button>
                                    )}
                                    {!isRejected && (
                                      <button
                                        onClick={() => handleVerifyRsvp(r, 'REJECTED')}
                                        className="px-2.5 py-1 bg-rose-600/30 hover:bg-rose-600 text-rose-300 hover:text-white font-bold text-[11px] rounded-lg border border-rose-500/40 transition cursor-pointer"
                                      >
                                        ❌ Reject
                                      </button>
                                    )}

                                    {r.visitor_phone && (
                                      <button
                                        onClick={() => sendVisitorWhatsApp(r.visitor_phone, r.visitor_name, r.verification_status || 'VERIFIED', rsvpModalData?.event?.title)}
                                        className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-600 text-emerald-300 hover:text-white font-bold text-[10px] rounded-lg border border-emerald-500/40 transition cursor-pointer flex items-center gap-1"
                                        title={`Send WhatsApp Message to ${r.visitor_name}`}
                                      >
                                        💬 WhatsApp
                                      </button>
                                    )}

                                    {r.visitor_email && (
                                      <button
                                        onClick={() => sendVisitorEmail(r.visitor_email, r.visitor_name, r.verification_status || 'VERIFIED', rsvpModalData?.event?.title)}
                                        className="px-2.5 py-1 bg-cyan-500/20 hover:bg-cyan-600 text-cyan-300 hover:text-white font-bold text-[10px] rounded-lg border border-cyan-500/40 transition cursor-pointer flex items-center gap-1"
                                        title={`Send Email Notification to ${r.visitor_name}`}
                                      >
                                        ✉️ Email
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-slate-400 mt-1 font-medium space-y-0.5">
                                  {r.substitute_name && (
                                    <p className="text-amber-300 font-bold">🔄 Substitute: {r.substitute_name} ({r.substitute_company || 'N/A'}) • 📞 {r.substitute_phone || 'N/A'}</p>
                                  )}
                                  {r.visitor_name && (
                                    <p className="text-cyan-300 font-bold">👥 Bringing Visitor: {r.visitor_name} ({r.visitor_company || 'N/A'}) • 📞 {r.visitor_phone || 'N/A'}</p>
                                  )}
                                </div>
                              )}
                            </div>

                            <span className="text-[10px] text-slate-500 font-semibold whitespace-nowrap">
                              {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const TextNewsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get("https://rmbbackend.rmbjaipur.com/auth/newsheading")
      .then(res => {
        if (res.data?.data?.[0]?.title) {
          setTitle(res.data.data[0].title);
        }
      })
      .catch(err => console.error("Failed to fetch news heading:", err));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      await axios.put("https://rmbbackend.rmbjaipur.com/auth/newsheading", { title }, { headers: { "Content-Type": "application/json" } });
      setLoading(false);
      onClose();
      alert('Text news announcement updated successfully!');
    } catch (err) {
      console.error("Failed to update news heading:", err);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-2xl border border-slate-200 space-y-4">
        <div className="flex justify-between items-center border-b border-slate-200 pb-3">
          <h3 className="text-lg font-black text-[#02256D]">📢 Add / Update Text News Ticker</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl font-bold cursor-pointer">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Text News Announcement</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter announcement text for home page ticker..."
              className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-semibold"
              required
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-[#FDBC12] hover:bg-amber-400 text-[#02256D] font-extrabold text-xs rounded-xl shadow-md cursor-pointer"
            >
              {loading ? 'Saving...' : 'Update Text News'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventsCalendar;
